// src/api/axios.js
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api',
  // SECURITY FIX (M3/H-1): the refresh token lives in an httpOnly cookie — withCredentials
  // lets the browser attach it to /auth/refresh automatically; JS never stores it.
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request Interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('amps_token');
  if (token && token !== 'undefined' && token !== 'null') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Check if it's a 401, not a retry, and not a login request
    if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
      
      if (isRefreshing) {
        // If already refreshing, wait for it to finish and then retry with new token
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          if (originalRequest.headers && originalRequest.headers.set) {
            originalRequest.headers.set('Authorization', 'Bearer ' + token);
          } else {
            originalRequest.headers.Authorization = 'Bearer ' + token;
          }
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      // Start refreshing process
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // SECURITY FIX (M3/H-1): the httpOnly cookie carries the refresh token; a legacy
        // localStorage token (from an older session) is sent once as fallback, then purged.
        const legacyRefreshToken = localStorage.getItem('amps_refresh_token');

        // Use a SEPARATE axios instance or raw fetch to avoid infinite interceptor loops
        const res = await axios.post(
          `${api.defaults.baseURL}/auth/refresh`,
          legacyRefreshToken ? { refreshToken: legacyRefreshToken } : {},
          { withCredentials: true }
        );
        
        const newAccessToken = res.data?.accessToken || res.data?.token || res.data?.data?.accessToken || res.data?.tokens?.accessToken;
        // (refresh token travels in the httpOnly cookie; nothing to store here)

        if (newAccessToken) {
          // 1. Save ONLY the short-lived access token; the rotated refresh token stays
          // in the httpOnly cookie (never in localStorage).
          localStorage.setItem('amps_token', newAccessToken);
          localStorage.removeItem('amps_refresh_token');
          
          // 2. Update defaults for future requests
          api.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;
          
          // 3. Update the original request's header specifically
          if (originalRequest.headers && originalRequest.headers.set) {
            originalRequest.headers.set('Authorization', 'Bearer ' + newAccessToken);
          } else {
            originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
          }
          
          // 4. Process queue and retry the original request
          processQueue(null, newAccessToken);
          return api(originalRequest); 
        } else {
           throw new Error("Token missing from refresh response");
        }

      } catch (refreshError) {
        // Refresh failed (e.g., token expired or 3-device limit reached)
        processQueue(refreshError, null);
        localStorage.removeItem('amps_token');
        localStorage.removeItem('amps_refresh_token');
        localStorage.removeItem('amps_user');
        window.location.href = '/login'; // Force redirect to login
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For any other error or if retry failed
    return Promise.reject(error);
  }
);

export default api;
